import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';

const URGENCY_STYLES = {
  critical: { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', dot: '#dc2626' },
  high: { bg: '#fffbeb', border: '#fde68a', color: '#d97706', dot: '#f59e0b' },
  medium: { bg: '#f8fafc', border: '#e2e8f0', color: '#64748b', dot: '#94a3b8' },
};

const METHOD_ICONS = {
  call: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  task: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
};

export default function SmartFollowUps({ navigate }) {
  const [data, setData] = useState(null);
  const isMobile = useIsMobile();
  const [futureProjects, setFutureProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stale');
  const [scheduling, setScheduling] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [followRes, futureRes] = await Promise.all([
        fetch(`${API}/api/sales/follow-ups/smart`, authOpts()),
        fetch(`${API}/api/sales/follow-ups/future-projects`, authOpts()),
      ]);
      if (followRes.ok) setData(await followRes.json());
      if (futureRes.ok) setFutureProjects(await futureRes.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scheduleFollowup = async (fp) => {
    if (!scheduleDate) return;
    const res = await fetch(`${API}/api/sales/follow-ups/schedule`, authOpts({
      method: 'POST',
      body: JSON.stringify({ opportunity_id: fp.opportunity_id, service: fp.service, follow_up_date: scheduleDate }),
    }));
    if (res.ok) {
      setScheduling(null);
      setScheduleDate('');
      load();
    }
  };

  if (loading) return <div data-testid="followups-loading" style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Loading follow-ups...</div>;

  const summary = data?.summary || {};
  const followups = data?.followups || [];

  return (
    <div data-testid="smart-followups-view" style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '12px 10px' : '24px 28px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>Smart Follow-Ups</h1>
        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Deals that need attention — prioritized by urgency and value at risk</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 8 : 12, marginBottom: 20 }}>
        <SummaryCard label="Critical" value={summary.critical || 0} color="#dc2626" />
        <SummaryCard label="High Priority" value={summary.high || 0} color="#f59e0b" />
        <SummaryCard label="Medium" value={summary.medium || 0} color="#94a3b8" />
        <SummaryCard label="Value at Risk" value={money(summary.total_value_at_risk)} color="#1e293b" isText />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 16 }}>
        {[
          { key: 'stale', label: `Stale Deals (${followups.length})` },
          { key: 'future', label: `Future Projects (${futureProjects.length})` },
        ].map(tab => (
          <button key={tab.key} data-testid={`followup-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: 'none', borderBottom: activeTab === tab.key ? '2px solid #c0392b' : '2px solid transparent',
              color: activeTab === tab.key ? '#c0392b' : '#64748b', marginBottom: -2,
            }}>{tab.label}</button>
        ))}
      </div>

      {/* Stale Deals */}
      {activeTab === 'stale' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {followups.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#16a34a', fontSize: 14, fontWeight: 600 }}>
              No stale deals — all leads are being actively worked!
            </div>
          ) : followups.map((fu, i) => {
            const us = URGENCY_STYLES[fu.urgency] || URGENCY_STYLES.medium;
            const iconPath = METHOD_ICONS[fu.suggested_method] || METHOD_ICONS.task;
            return (
              <div key={i} data-testid={`followup-row-${i}`}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 140px 120px 80px 1fr 50px',
                  gap: 12, alignItems: 'center', padding: '12px 14px', borderRadius: 8,
                  background: us.bg, border: `1px solid ${us.border}`,
                }}>
                {/* Deal Info */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{fu.title}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{fu.customer_name}</div>
                </div>
                {/* Stage */}
                <div>
                  <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>
                    {fu.stage.replace(/_/g, ' ')}
                  </span>
                </div>
                {/* Stale Days */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: us.color }}>{fu.days_stale}d</div>
                  <div style={{ fontSize: 9, color: '#94a3b8' }}>stale ({fu.threshold}d threshold)</div>
                </div>
                {/* Value */}
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', textAlign: 'right' }}>{money(fu.value)}</div>
                {/* Action */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={us.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={iconPath} />
                  </svg>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: us.color }}>{fu.next_best_action}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{fu.action_message.slice(0, 60)}</div>
                  </div>
                </div>
                {/* Link */}
                <button data-testid={`followup-goto-${i}`} onClick={() => navigate(`/sales/opportunity/${fu.opportunity_id}`)}
                  style={{ padding: '5px 8px', fontSize: 10, fontWeight: 700, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  Open
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Future Projects */}
      {activeTab === 'future' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {futureProjects.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
              No future projects detected yet. Run expansion analysis on your deals to surface them.
            </div>
          ) : futureProjects.map((fp, i) => (
            <div key={i} data-testid={`future-project-${i}`}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px',
                borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0',
              }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{fp.service}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{fp.customer_name} — {fp.opportunity_title}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  Timing: {fp.approximate_timing} | Source: {fp.source}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {fp.followup_scheduled ? (
                  <span style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, background: '#f0fdf4', color: '#16a34a', borderRadius: 4, border: '1px solid #bbf7d0' }}>
                    Scheduled
                  </span>
                ) : scheduling === i ? (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="date" data-testid={`schedule-date-${i}`} value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                      style={{ padding: '4px 6px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 4 }} />
                    <button data-testid={`schedule-confirm-${i}`} onClick={() => scheduleFollowup(fp)}
                      style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Save
                    </button>
                    <button onClick={() => { setScheduling(null); setScheduleDate(''); }}
                      style={{ padding: '4px 8px', fontSize: 10, background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button data-testid={`schedule-btn-${i}`} onClick={() => setScheduling(i)}
                    style={{ padding: '5px 12px', fontSize: 10, fontWeight: 700, background: '#1e293b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Schedule Follow-Up
                  </button>
                )}
                <button onClick={() => navigate(`/sales/opportunity/${fp.opportunity_id}`)}
                  style={{ padding: '5px 8px', fontSize: 10, fontWeight: 700, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, isText }) {
  return (
    <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: isText ? 18 : 28, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
